import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { useApiQuery } from '../useApiQuery';
import { apiDelete, apiGet, apiPost, operationsClient } from '@/lib/api';
import {
  ALLOWED_ATTACHMENT_TYPES,
  MAX_ATTACHMENT_BYTES,
  normalizeAttachmentFile,
  validateAttachmentFile,
  type AttachmentFile,
} from '@/lib/attachments/attachment-files';
import { contactKeys } from '@/lib/queryKeys';

export const MAX_NOTE_ATTACHMENT_BYTES = MAX_ATTACHMENT_BYTES;
export const ALLOWED_NOTE_ATTACHMENT_TYPES = ALLOWED_ATTACHMENT_TYPES;

export type NoteAttachment = AttachmentFile;

export function isPreviewableNoteAttachment(att: NoteAttachment) {
  return att.mimeType.startsWith('image/');
}

function noteAttachmentsKey(contactId: string, noteId: string) {
  return [...contactKeys.detail(contactId), 'note-attachments', noteId] as const;
}

export function useNoteAttachments(contactId: string, noteId: string, enabled = true) {
  return useApiQuery<NoteAttachment[]>({
    queryKey: noteAttachmentsKey(contactId, noteId),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>[]>(
        operationsClient,
        `/contacts/${contactId}/notes/${noteId}/attachments`,
      );
      return raw.map(normalizeAttachmentFile);
    },
    enabled: enabled && Boolean(contactId && noteId),
    showErrorToast: false,
  });
}

export async function getNoteAttachmentDownloadUrl(
  contactId: string,
  noteId: string,
  attachmentId: string,
) {
  const raw = await apiGet<Record<string, unknown>>(
    operationsClient,
    `/contacts/${contactId}/notes/${noteId}/attachments/${attachmentId}/download-url`,
  );
  return String(raw.downloadUrl ?? raw.url ?? '');
}

/** Validate a file client-side before uploading. Returns an error message or null. */
export function validateNoteAttachment(file: File): string | null {
  return validateAttachmentFile(file);
}

/** Upload a single file to a note's attachments (presigned PUT). Used directly
 * in the create flow once the note id is known. */
export async function uploadNoteFile(
  contactId: string,
  noteId: string,
  file: File,
): Promise<NoteAttachment> {
  const error = validateNoteAttachment(file);
  if (error) throw new Error(error);

  const raw = await apiPost<Record<string, unknown>>(
    operationsClient,
    `/contacts/${contactId}/notes/${noteId}/attachments/upload-url`,
    { fileName: file.name, contentType: file.type, fileSize: file.size },
  );

  const uploadUrl = String(raw.uploadUrl ?? '');
  const attachment = normalizeAttachmentFile((raw.attachment ?? {}) as Record<string, unknown>);

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!response.ok) throw new Error(`Upload failed (${response.status})`);

  return attachment;
}

export function useUploadNoteFile(contactId: string, noteId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<NoteAttachment, File>({
    mutationFn: (file) => uploadNoteFile(contactId, noteId, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: noteAttachmentsKey(contactId, noteId) });
      void queryClient.invalidateQueries({ queryKey: contactKeys.notes(contactId) });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to upload attachment');
    },
  });
}

export function useDeleteNoteFile(contactId: string, noteId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<{ deleted: boolean }, string>({
    mutationFn: (attachmentId) =>
      apiDelete<{ deleted: boolean }>(
        operationsClient,
        `/contacts/${contactId}/notes/${noteId}/attachments/${attachmentId}`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: noteAttachmentsKey(contactId, noteId) });
      void queryClient.invalidateQueries({ queryKey: contactKeys.notes(contactId) });
      toast.success('Attachment deleted');
    },
  });
}
